import * as XLSX from "xlsx";

import { statusLabels } from "@/constant/statusConstants";

import { normalizeString } from "./helper";

export const parseImportFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        let workbook;

        if (file.name.endsWith(".csv")) {
          const csvData = e.target.result;
          workbook = XLSX.read(csvData, { type: "string" });
        } else {
          const data = new Uint8Array(e.target.result);
          workbook = XLSX.read(data, { type: "array" });
        }

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Normalize all cells in the worksheet
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let row = range.s.r; row <= range.e.r; row++) {
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            const cell = worksheet[cellAddress];

            if (cell && cell.v) {
              if (row === range.s.r) {
                // Normalize and lowercase the Headers (first row)
                cell.v = normalizeString(cell.v).toLowerCase();
              } else {
                // Normalize the values
                cell.v = normalizeString(cell.v);
              }

              cell.w = cell.v; // Update the displayed value
            }
          }
        }

        // Convert the worksheet to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        resolve(jsonData);
      } catch (error) {
        reject(error);
      }
    };

    if (file.name.endsWith(".csv")) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
};

export const validateBlankColumns = ({ data, requiredColumns }) => {
  const errors = [];
  const normalizedRequiredColumns = requiredColumns.map((field) =>
    field.toLowerCase()
  );

  data.forEach((row, index) => {
    const rowNumber = index + 2; // Adding 2 to account for the header row and 0-based index
    normalizedRequiredColumns.forEach((key, fieldIndex) => {
      if (
        !(key in row) ||
        row[key] === undefined ||
        row[key] === null ||
        row[key].toString().trim() === ""
      ) {
        errors.push({
          row: rowNumber,
          error: `${requiredColumns[fieldIndex]} column is blank.`,
        });
      }
    });
  });

  return errors;
};
export const vaidateDuplicateColumn = ({ data, shouldUniqueColumn }) => {
  const errors = [];
  const columnMap = new Map();

  const normalizedShouldUniqueColumn = shouldUniqueColumn.toLowerCase();

  data.forEach((row, index) => {
    const rowNumber = index + 2; // Account for header and 0-based index
    const value = row[normalizedShouldUniqueColumn];

    if (
      value !== undefined &&
      value !== null &&
      value.toString().trim() !== ""
    ) {
      if (columnMap.has(value)) {
        errors.push({
          row: rowNumber,
          error: `Duplicate ${shouldUniqueColumn} "${value}" found in the import file. Previous occurrence at row ${columnMap.get(
            value
          )}.`,
        });
      } else {
        columnMap.set(value, rowNumber);
      }
    }
  });

  return errors;
};
export const validateColumnOptionValues = ({
  data,
  columnName,
  validValues,
}) => {
  const errors = [];

  data.forEach((row, index) => {
    const rowNumber = index + 2; // Account for header and 0-based index

    if (!validValues.includes(row[columnName])) {
      errors.push({
        row: rowNumber,
        error: `The ${columnName} column value must be one of: ${validValues.join(
          ", "
        )}.`,
      });
    }
  });

  return errors;
};

export const validateMasterImportData = (data, fieldNames, codeFieldName) => {
  const errors = [];
  const idMap = new Map();

  const normalizedFieldNames = fieldNames.map((field) => field.toLowerCase());
  const normalizedCodeFieldName = codeFieldName.toLowerCase();
  const statusLabelsMap = Object.values(statusLabels());

  data.forEach((row, index) => {
    const rowNumber = index + 2;
    const id = normalizeString(row[normalizedCodeFieldName]);

    normalizedFieldNames.forEach((key, index) => {
      if (!(key in row)) {
        errors.push({
          row: rowNumber,
          error: `${fieldNames[index]} column is blank.`,
        });
      }

      if (key === "status" && !statusLabelsMap.includes(row[key])) {
        errors.push({
          row: rowNumber,
          error: `The ${
            fieldNames[index]
          } column value must be one of: ${statusLabelsMap.join(", ")}.`,
        });
      }
    });

    if (id !== undefined && id !== null && id !== "") {
      if (idMap.has(id)) {
        errors.push({
          row: rowNumber,
          error: `Duplicate ${codeFieldName} "${id}" found in the import file. Previous occurrence at row ${idMap.get(
            id
          )}.`,
        });
      } else {
        idMap.set(id, rowNumber);
      }
    }
  });

  return errors;
};
export const validateMappingImportData = (
  data,
  columns,
  nullableColumns = [],
  shouldUniqueColumn = null,
  shouldBeUniqueCompositeColumns = []
) => {
  const errors = [];
  const idMap = new Map();
  const compositeKeyMap = new Map();

  const normalizedColumns = columns.map((column) => column.toLowerCase());
  const normalizedShouldUniqueColumn = shouldUniqueColumn
    ? shouldUniqueColumn.toLowerCase()
    : null;
  const normalizedCompositeColumns = shouldBeUniqueCompositeColumns.map(
    (column) => column.toLowerCase()
  );

  data.forEach((row, index) => {
    const rowNumber = index + 2;

    normalizedColumns.forEach((key, index) => {
      if (!(key in row) && !nullableColumns.includes(columns[index])) {
        errors.push({
          row: rowNumber,
          error: `${columns[index]} column is blank.`,
        });
      }
    });

    if (normalizedShouldUniqueColumn) {
      const id = normalizeString(row[normalizedShouldUniqueColumn]);
      if (id !== undefined && id !== null && id !== "") {
        if (idMap.has(id)) {
          errors.push({
            row: rowNumber,
            error: `Duplicate ${shouldUniqueColumn} "${id}" found in the import file. Previous occurrence at row ${idMap.get(
              id
            )}.`,
          });
        } else {
          idMap.set(id, rowNumber);
        }
      }
    }

    if (normalizedCompositeColumns.length > 0) {
      const compositeKey = normalizedCompositeColumns
        .map((column) => normalizeString(row[column] ?? ""))
        .join("|");

      if (compositeKeyMap.has(compositeKey)) {
        const errorDetails = normalizedCompositeColumns
          .map((column) => `${column}: ${row[column] || "N/A"}`)
          .join(", ");

        errors.push({
          row: rowNumber,
          error: `Duplicate composite key (${shouldBeUniqueCompositeColumns.join(
            ", "
          )}) found: ${errorDetails}. Previous occurrence at row ${compositeKeyMap.get(
            compositeKey
          )}.`,
        });
      } else {
        compositeKeyMap.set(compositeKey, rowNumber);
      }
    }
  });

  return errors;
};
